<template>
    <p class="sr-only" role="status">
        <template v-if="loading && !previewEmpty">{{ t("peopleDirectory.teamSuggestions.loading") }}</template>
        <template v-else-if="displayState === 'empty'">{{ t("peopleDirectory.teamSuggestions.empty") }}</template>
        <template v-else-if="displayState === 'ready'">{{ t("peopleDirectory.teamSuggestions.ready", { count: suggestions.length }) }}</template>
    </p>
    <p class="sr-only" role="alert">
        <template v-if="failed && !previewEmpty">{{ rateLimited ? t("peopleDirectory.teamSuggestions.rateLimited") : t("peopleDirectory.teamSuggestions.error") }}</template>
    </p>
    <section ref="suggestionsSection" tabindex="-1" class="flex flex-col gap-4" :aria-label="t('peopleDirectory.teamSuggestions.title')" :aria-busy="loading">
        <BaseCard v-if="displayState !== 'ready'" padding="md" radius="lgr" theme="muted">
            <div class="flex items-start gap-3">
                <i
                    :class="['bi text-ink-muted', displayState === 'loading' ? 'bi-calendar3' : displayState === 'error' ? 'bi-arrow-clockwise' : 'bi-calendar2-check']"
                    aria-hidden="true" />
                <div class="flex min-w-0 flex-col items-start gap-2">
                    <template v-if="displayState === 'loading'">
                        <p class="body-small-semibold text-ink" aria-hidden="true">{{ t("peopleDirectory.teamSuggestions.loading") }}</p>
                        <p class="body-small text-ink-muted">{{ t("peopleDirectory.teamSuggestions.loadingHint") }}</p>
                    </template>
                    <template v-else-if="displayState === 'error'">
                        <p class="body-small-semibold text-ink">{{ t("peopleDirectory.teamSuggestions.errorTitle") }}</p>
                        <p class="body-small text-ink-muted" aria-hidden="true">
                            {{ rateLimited ? t("peopleDirectory.teamSuggestions.rateLimited") : t("peopleDirectory.teamSuggestions.error") }}
                        </p>
                        <BaseButton theme="secondary" size="sm" :disabled="disabled" @click="retry">{{ t("peopleDirectory.retry") }}</BaseButton>
                    </template>
                    <template v-else>
                        <p class="body-small-semibold text-ink">{{ t("peopleDirectory.teamSuggestions.emptyTitle") }}</p>
                        <p class="body-small text-ink-muted" aria-hidden="true">{{ t("peopleDirectory.teamSuggestions.empty") }}</p>
                    </template>
                </div>
            </div>
        </BaseCard>
        <p class="body-small text-ink-muted flex items-start gap-2 empty:sr-only" role="status">
            <template v-if="!previewEmpty && (degraded || (failed && suggestions.length > 0))"
                ><i class="bi bi-info-circle shrink-0" aria-hidden="true" />{{ t("peopleDirectory.teamSuggestions.fallback") }}</template
            >
        </p>
        <BaseButton
            v-if="!previewEmpty && (degraded || (failed && suggestions.length > 0))"
            theme="secondary"
            size="sm"
            class="self-start"
            :disabled="disabled || loading"
            @click="retry">
            {{ t("peopleDirectory.retry") }}
        </BaseButton>
        <ul v-if="displayState === 'ready'" class="flex flex-col gap-4">
            <li v-for="(suggestion, index) in suggestions" :key="suggestion.kind === 'join' ? suggestion.team_id : index">
                <BaseCard padding="sm" radius="lgr">
                    <div class="@container flex flex-col gap-6">
                        <!-- Narrow cards (phones, the chat drawer) stack the title and explanation and put the action after the people; wider cards keep it beside the explanation. -->
                        <div class="grid grid-cols-1 items-start gap-x-4 gap-y-2 @lg:grid-cols-[minmax(0,1fr)_auto] @lg:gap-y-0">
                            <h3 class="body-serif text-ink col-start-1 row-start-1 break-words pb-1">
                                {{ suggestion.name
                                }}<span v-if="isAdded(suggestion)" class="body text-ink-muted">&nbsp;{{ t("peopleDirectory.teamSuggestions.draftCreated") }}</span>
                            </h3>
                            <p class="body-small text-ink-muted col-start-1 row-start-2 @lg:max-w-[80cqw]">{{ suggestion.reason }}</p>
                            <div class="col-start-2 row-span-2 row-start-1 hidden flex-wrap items-center justify-end gap-2 @lg:flex">
                                <BaseButton
                                    theme="neutral"
                                    size="sm"
                                    :disabled="disabled || (suggestion.kind !== 'join' && createDisabled)"
                                    :aria-label="actionLabel(suggestion, true)"
                                    @click="select(suggestion)">
                                    {{ actionLabel(suggestion) }}
                                </BaseButton>
                            </div>
                        </div>
                        <!-- Columns of three wrap onto a new row instead of running past the card; the roles keep one list for screen readers. -->
                        <div role="list" class="flex flex-col gap-2 @lg:flex-row @lg:flex-wrap @lg:gap-x-10">
                            <div v-for="(column, columnIndex) in memberColumns(suggestion)" :key="columnIndex" role="none" class="flex min-w-0 flex-col gap-2">
                                <div v-for="member in column" :key="member.id" role="listitem" class="flex max-w-full items-center gap-2">
                                    <PerspectiveAvatar v-if="member.candidate" :name="member.name" :letters="member.candidate.perspective_letters" size="2xs" />
                                    <BaseAvatar v-else size="2xs" disabled :initials="getAvatarInitials(member.name).charAt(0)" />
                                    <BaseTooltip v-if="!member.candidate" open-on-click>
                                        <template #trigger>
                                            <span tabindex="0" class="body-small text-ink-muted min-w-0 cursor-help break-words">{{ member.name }}</span>
                                        </template>
                                        <template #content>{{ t("peopleDirectory.teamSuggestions.notNadiaUser") }}</template>
                                    </BaseTooltip>
                                    <span v-else class="body-small text-ink-muted min-w-0 break-words">{{ member.name }}</span>
                                </div>
                                <p
                                    v-if="suggestion.members.length > MAX_VISIBLE_MEMBERS && columnIndex === memberColumns(suggestion).length - 1"
                                    role="listitem"
                                    class="body-small text-ink-muted flex min-h-6 items-center">
                                    {{ t("peopleDirectory.teamSuggestions.otherPeople", { count: suggestion.members.length - MAX_VISIBLE_MEMBERS }) }}
                                </p>
                            </div>
                        </div>
                        <BaseButton
                            theme="neutral"
                            size="sm"
                            class="w-full @lg:hidden"
                            :disabled="disabled || (suggestion.kind !== 'join' && createDisabled)"
                            :aria-label="actionLabel(suggestion, true)"
                            @click="select(suggestion)">
                            {{ actionLabel(suggestion) }}
                        </BaseButton>
                    </div>
                </BaseCard>
            </li>
        </ul>
        <DemoToolsLauncher v-if="demoToolsVisible" v-model:open="demoToolsOpen" :title="t('peopleDirectory.teamSuggestions.demoTitle')">
            <div class="flex flex-col gap-3">
                <h2 class="body-small-semibold text-ink">{{ t("peopleDirectory.teamSuggestions.demoTitle") }}</h2>
                <p class="body-small text-ink-muted">{{ t("peopleDirectory.teamSuggestions.demoHint") }}</p>
                <p class="body-small text-ink-muted">{{ t("peopleDirectory.teamSuggestions.regenerateHint") }}</p>
                <BaseButton theme="secondary" size="sm" :disabled="disabled || loading" @click="regenerate">
                    {{ t("peopleDirectory.teamSuggestions.regenerate") }}
                </BaseButton>
                <BaseButton
                    theme="secondary"
                    size="sm"
                    @click="
                        previewEmpty = !previewEmpty;
                        demoToolsOpen = false;
                    ">
                    {{ previewEmpty ? t("peopleDirectory.teamSuggestions.restoreRecommendations") : t("peopleDirectory.teamSuggestions.showEmpty") }}
                </BaseButton>
            </div>
        </DemoToolsLauncher>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { ExistingTeamRecommendation, TeamRecommendation, TeamRecommendations, TeamRecommendationsRequest } from "~vue/api/generated/team-perspective/types.gen";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import PerspectiveAvatar from "~vue/components/PerspectiveAvatar.vue";
import DemoToolsLauncher from "~vue/DemoTools/DemoToolsLauncher.vue";
import { TEAM_INTERACTION_SOURCE, type TeamInteractionSource } from "~vue/Teams/teamInteraction";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { getAvatarInitials } from "~vue/utils/avatarUtils";
import { logUserInteraction } from "~vue/utils/logUtils";
import chunk from "lodash/chunk";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const {
    source = TEAM_INTERACTION_SOURCE.MY_PEOPLE,
    url,
    disabled = false,
    createDisabled = false,
    selectedMemberIds = [],
    selectedUnavailableIds = [],
    demoToolsVisible = true,
} = defineProps<{
    source?: TeamInteractionSource;
    url: string;
    disabled?: boolean;
    createDisabled?: boolean;
    selectedMemberIds?: string[];
    selectedUnavailableIds?: string[];
    demoToolsVisible?: boolean;
}>();
type Suggestion = TeamRecommendations["recommendations"][number];
const emit = defineEmits<{ select: [suggestion: TeamRecommendation]; join: [suggestion: ExistingTeamRecommendation]; review: [] }>();
const { t } = useI18n();
const suggestionsSection = useTemplateRef<HTMLElement>("suggestionsSection");
const loading = ref(false);
const failed = ref(false);
const rateLimited = ref(false);
const degraded = ref(false);
const suggestions = ref<Suggestion[]>([]);
const demoToolsOpen = ref(false);
const previewEmpty = ref(false);
const lastRequest = ref<TeamRecommendationsRequest>({});
const displayState = computed(() => {
    if (previewEmpty.value) {
        return "empty";
    }
    if (suggestions.value.length) {
        return "ready";
    }
    if (loading.value) {
        return "loading";
    }
    if (failed.value) {
        return "error";
    }
    return "empty";
});

const MEMBERS_PER_COLUMN = 3;
const MAX_VISIBLE_MEMBERS = 5;

function memberColumns(suggestion: Suggestion) {
    return chunk(suggestion.members.slice(0, MAX_VISIBLE_MEMBERS), MEMBERS_PER_COLUMN);
}

function isAdded(suggestion: Suggestion) {
    return (
        suggestion.kind !== "join" &&
        suggestion.members.length > 0 &&
        suggestion.members.every((member) => (member.candidate ? selectedMemberIds.includes(member.candidate.id) : selectedUnavailableIds.includes(member.id)))
    );
}

async function load(payload: TeamRecommendationsRequest = {}) {
    if (loading.value || disabled) {
        return;
    }
    lastRequest.value = payload;
    loading.value = true;
    failed.value = false;
    rateLimited.value = false;
    try {
        const { data, error, response } = await useFetch(url).post(payload).json<TeamRecommendations>();
        if (error.value || !data.value) {
            failed.value = true;
            rateLimited.value = response.value?.status === 429;
            return;
        }
        suggestions.value = data.value.recommendations;
        degraded.value = data.value.degraded ?? false;
    } catch {
        failed.value = true;
    } finally {
        loading.value = false;
    }
}

function retry() {
    if (loading.value || disabled) {
        return;
    }
    logUserInteraction(AmplitudeEventNames.TEAM_SUGGESTIONS_CLICKED, { source, interaction: "retry" });
    suggestionsSection.value?.focus();
    void load(lastRequest.value);
}

function regenerate() {
    if (loading.value || disabled) {
        return;
    }
    logUserInteraction(AmplitudeEventNames.TEAM_SUGGESTIONS_CLICKED, { source, interaction: "regenerate" });
    demoToolsOpen.value = false;
    previewEmpty.value = false;
    void load({ regenerate: true });
}

function actionLabel(suggestion: Suggestion, named = false) {
    if (suggestion.kind === "join") {
        return named ? t("peopleDirectory.teamSuggestions.joinNamed", { name: suggestion.name }) : t("peopleDirectory.teamSuggestions.join");
    }
    if (isAdded(suggestion)) {
        return named ? t("peopleDirectory.teamSuggestions.reviewNamed", { name: suggestion.name }) : t("peopleDirectory.teamSuggestions.review");
    }
    return named ? t("peopleDirectory.teamSuggestions.useNamed", { name: suggestion.name }) : t("peopleDirectory.teamSuggestions.use");
}

function select(suggestion: Suggestion) {
    if (disabled) {
        return;
    }
    if (suggestion.kind === "join") {
        emit("join", suggestion);
    } else if (!createDisabled) {
        if (isAdded(suggestion)) {
            emit("review");
        } else {
            emit("select", suggestion);
        }
    }
}

onMounted(() => load());
</script>
